import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiHome,
  FiAlertCircle,
  FiBox,
  FiUsers,
  FiRepeat,
  FiLogOut,
  FiMenu,
  FiX
} from 'react-icons/fi';
import logo from '../css/images.png';

const Sidebar = ({ onToggle }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth <= 768);
  const sidebarRef = useRef(null);
  const location = useLocation();

  const handleSignOut = () => {
    localStorage.clear();
    window.location.replace('/');
  };

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(prev => !prev);
  };

  const handleLinkClick = () => {
    if (window.innerWidth <= 768) {
      setIsSidebarCollapsed(true);
    }
  };

  const handleResize = () => {
    if (window.innerWidth <= 768) {
      setIsSidebarCollapsed(true);
    } else {
      setIsSidebarCollapsed(false);
    }
  };

  const handleClickOutside = useCallback(
    (event) => {
      if (
        window.innerWidth <= 768 &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !isSidebarCollapsed
      ) {
        setIsSidebarCollapsed(true);
      }
    },
    [isSidebarCollapsed]
  );

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (onToggle) {
      onToggle(isSidebarCollapsed);
    }
  }, [isSidebarCollapsed, onToggle]);

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <FiHome size={20} /> },
    { path: '/outstanding', label: 'Outstanding', icon: <FiAlertCircle size={20} /> },
    { path: '/stock-report', label: 'Stock', icon: <FiBox size={20} /> },
    { path: '/salesman-report', label: 'Salesman Report', icon: <FiUsers size={20} /> },
    { path: '/year-select', label: 'Switch Company', icon: <FiRepeat size={20} /> },
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={handleToggleSidebar}
        className={`fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md text-slate-600 md:hidden hover:bg-slate-50 transition-all duration-300 ${
          !isSidebarCollapsed ? 'hidden' : ''
        }`}
      >
        <FiMenu size={24} />
      </button>

      {/* Sidebar */}
      <nav
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full bg-white text-slate-700 shadow-2xl transition-all duration-300 z-40 flex flex-col border-r border-slate-100
          ${
            isSidebarCollapsed
              ? '-translate-x-full md:translate-x-0 md:w-[80px]'
              : 'translate-x-0 w-[260px]'
          }
        `}
      >
        {/* Logo Header */}
        <div className="bg-white relative">
          <div className="h-24 flex items-center justify-center px-6">
            <div
              className={`flex items-center justify-center w-full overflow-hidden transition-all duration-300 ${
                isSidebarCollapsed ? 'px-2' : ''
              }`}
            >
              <img
                src={logo}
                alt="DeviSoft"
                className={`object-contain transition-all duration-300 ${
                  isSidebarCollapsed ? 'w-12 h-12' : 'w-56 h-20'
                }`}
              />
            </div>

            <button
              onClick={handleToggleSidebar}
              className={`absolute right-4 text-slate-400 hover:text-slate-600 md:hidden ${
                isSidebarCollapsed ? 'hidden' : ''
              }`}
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Divider under logo */}
          <hr className="border-slate-200 mx-6" />
        </div>

        {/* Menu */}
        <div className="flex-1 overflow-y-auto py-6">
          <ul className="space-y-2 px-3">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`flex items-center gap-3 px-3 py-3.5 rounded-xl transition-all duration-200 group relative font-medium
                      ${
                        isActive
                          ? 'bg-blue-200 text-white shadow-lg shadow-blue-100/30'
                          : 'hover:bg-blue-100 text-slate-600 hover:text-black'
                      }
                      ${isSidebarCollapsed ? 'justify-center' : ''}
                    `}
                  >
                    {/* Icon */}
                    <span
                      className={`transition-colors duration-200 ${
                        isActive
                          ? 'text-black'
                          : 'text-slate-600 group-hover:text-black'
                      }`}
                    >
                      {item.icon}
                    </span>

                    {/* Label */}
                    <span
                      className={`whitespace-nowrap transition-all duration-300 ${
                        isActive ? 'text-black' : ''
                      } ${isSidebarCollapsed ? 'hidden' : 'block'}`}
                    >
                      {item.label}
                    </span>

                    {/* Tooltip (collapsed) */}
                    {isSidebarCollapsed && (
                      <div className="absolute left-full ml-3 px-3 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-xl">
                        {item.label}
                        <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
                      </div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Logout */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={handleSignOut}
            className={`flex items-center gap-3 w-full px-3 py-3 rounded-xl !text-red-500 hover:bg-red-50 hover:text-red-600 transition-all duration-200 ${
              isSidebarCollapsed ? 'justify-center' : ''
            }`}
          >
            <FiLogOut size={20} />
            <span
              className={`whitespace-nowrap font-medium transition-all duration-300 ${
                isSidebarCollapsed ? 'hidden' : 'block'
              }`}
            >
              Logout
            </span>
          </button>
        </div>
      </nav>

      {/* Mobile Overlay */}
      {!isSidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}
    </>
  );
};

export default Sidebar;
